
import { useState } from "react";
import { isAxiosError } from "axios";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  Button,
  ScrollArea,
  Input,
  Label,
  Textarea,
  MoneyInput,
  Switch,
} from "@/components/ui";

import {
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useToast
} from "@/hooks";
import { LeadsValidationSchema } from "@/shared";
import { DisplayError } from "@/utils";
import { LeadFormProps } from "@/types";

import { MessageError } from "../../message-error";
import { identityPhone } from "@/lib/identity-validation";

export function FormLead({ lead, handleClose }: LeadFormProps) {
  const { t } = useTranslation();
  const [sharedPhone, setSharedPhone] = useState<string | null>(null);
  const [acknowledgedPhone, setAcknowledgedPhone] = useState<string | null>(null);
  const { toast } = useToast();

  const { mutateAsync: createLead, isLoading: isCreating } = useCreateLeadMutation();
  const { mutateAsync: updateLead, isLoading: isUpdating } = useUpdateLeadMutation();
  const isLoading = isCreating || isUpdating;

  const initialValues = {
    name: lead?.name || "",
    email: lead?.email || "",
    phone: identityPhone(lead?.phone ?? '') || "",
    company: lead?.company || "",
    source: lead?.source || "",
    status: lead?.status || "new",
    converted_to_patient: lead?.converted_to_patient || false,
    estimated_value: lead?.estimated_value || undefined,
    description: lead?.description || "",
  };

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: LeadsValidationSchema(),
    onSubmit: async (values) => {
      try {
        const phone = identityPhone(values.phone);
        const data = { ...values, phone, acknowledge_shared_phone: acknowledgedPhone === phone };
        if (lead) {
          await updateLead({
            id: lead.id,
            data
          });
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
        } else {
          await createLead(data);
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
        }
        handleClose();
        formik.resetForm();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 409 && error.response.data?.code === "lead_phone_shared") {
          setSharedPhone(identityPhone(values.phone));
          setAcknowledgedPhone(null);
          return;
        }
        toast({
          title: t("LABEL.error"),
          description: DisplayError(error, t("MESSAGE.error_message")),
          variant: "destructive",
        });
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  const statusOptions = [
    { value: "new", label: "Novo" },
    { value: "contacted", label: "Contatado" },
    { value: "qualified", label: "Qualificado" },
    { value: "converted", label: "Convertido" },
  ];

  const sourceOptions = [
    { value: "site", label: "Site" },
    { value: "indicacao", label: "Indicação" },
    { value: "rede_social", label: "Rede Social" },
    { value: "email", label: "E-mail Marketing" },
    { value: "telefone", label: "Telefone" },
    { value: "evento", label: "Evento" },
    { value: "WhatsApp", label: "WhatsApp" },
    { value: "outro", label: "Outro" },
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[50vh] md:max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-2"
    >
      <ScrollArea className=" h-full">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label required>Nome</Label>
              <Input
                isInvalid={formik.touched.name && !!formik.errors.name}
                isValid={formik.touched.name && !formik.errors.name}
                messageError={formik.errors.name as string}
                {...formik.getFieldProps("name")}
              />
            </div>

            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input
                type="email"
                isInvalid={formik.touched.email && !!formik.errors.email}
                isValid={formik.touched.email && !formik.errors.email}
                messageError={formik.errors.email as string}
                {...formik.getFieldProps("email")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead-phone">Telefone</Label>
              <Input
                id="lead-phone"
                inputPhone
                isInvalid={formik.touched.phone && !!formik.errors.phone}
                isValid={formik.touched.phone && !formik.errors.phone}
                messageError={formik.errors.phone as string}
                {...formik.getFieldProps("phone")}
                onChange={event => {
                  setSharedPhone(null);
                  setAcknowledgedPhone(null);
                  formik.handleChange(event);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Empresa</Label>
              <Input
                isInvalid={formik.touched.company && !!formik.errors.company}
                isValid={formik.touched.company && !formik.errors.company}
                messageError={formik.errors.company as string}
                {...formik.getFieldProps("company")}
              />
            </div>

            <div className="space-y-2">
              <Label>Origem</Label>
              <div className="space-y-1">
                <Select
                  {...formik.getFieldProps("source")}
                  onValueChange={(value) => {
                    formik.setFieldValue("source", value);
                  }}
                  defaultValue={formik.values.source}
                >
                  <SelectTrigger
                    isInvalid={formik.touched.source && !!formik.errors.source}
                    isValid={formik.touched.source && !formik.errors.source}
                  >
                    <SelectValue placeholder="Selecione a origem" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {sourceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError messageError={formik.errors.source as string} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <div className="space-y-1">
                <Select
                  {...formik.getFieldProps("status")}
                  onValueChange={(value) => {
                    formik.setFieldValue("status", value);
                  }}
                  defaultValue={formik.values.status}
                >
                  <SelectTrigger
                    isInvalid={formik.touched.status && !!formik.errors.status}
                    isValid={formik.touched.status && !formik.errors.status}
                  >
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <MessageError messageError={formik.errors.status as string} />
              </div>
            </div>

            <div className="space-y-2 col-span-2">
              <Label>Valor Estimado</Label>
              <MoneyInput
                defaultValue={formik.values.estimated_value}
                isInvalid={formik.touched.estimated_value && !!formik.errors.estimated_value}
                isValid={formik.touched.estimated_value && !formik.errors.estimated_value}
                messageError={formik.errors.estimated_value as string}
                {...formik.getFieldProps("estimated_value")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea
              rows={4}
              isInvalid={formik.touched.description && !!formik.errors.description}
              isValid={formik.touched.description && !formik.errors.description}
              messageError={formik.errors.description as string}
              {...formik.getFieldProps("description")}
            />
          </div>

          {sharedPhone !== null && sharedPhone === identityPhone(formik.values.phone) && (
            <div role="alert" className="space-y-2 rounded-lg border border-app-border p-3">
              <p>Já existe outro lead com este telefone nesta clínica. Confira o cadastro antes de salvar.</p>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={acknowledgedPhone === sharedPhone}
                  onChange={event => setAcknowledgedPhone(event.target.checked ? sharedPhone : null)} />
                Este número é compartilhado. Quero manter os cadastros separados.
              </label>
            </div>
          )}

          <div className="space-y-2 flex items-center gap-1.5">
            <Switch
              id="converted_to_patient"
              checked={formik.values.converted_to_patient}
              onCheckedChange={(checked) => {
                formik.setFieldValue("converted_to_patient", checked);
              }}
            />
            <Label htmlFor="converted_to_patient">Convertido para Paciente</Label>
          </div>
        </div>
      </ScrollArea>

      <div className="flex items-center justify-end gap-x-6">
        <Button
          type="submit"
          className="px-10"
          disabled={isLoading || formik.isSubmitting || (sharedPhone !== null && sharedPhone === identityPhone(formik.values.phone) && acknowledgedPhone !== sharedPhone)}
          isLoading={isLoading}
        >
          {lead ? t("LABEL.save") : t("LABEL.create")}
        </Button>
      </div>
    </form>
  );
}